import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useApiClient } from "@/api/client"
import { useWorkspace } from "@/workspaces/WorkspaceContext"
import type { Person } from "./usePeople"

type CreatePersonInput = {
  name: string
  email?: string
}

type PersonRecordResponse = {
  id: string
  workspace_id: string
  object_type: "person"
  created_at: string
  updated_at: string
  attributes: Record<string, unknown>
}

function mapRecordToPerson(record: PersonRecordResponse): Person {
  const attributes = record.attributes as {
    name?: string
    email?: string
    avatar_url?: string
    user_id?: string
  }

  return {
    id: record.id,
    name: attributes.name ?? "",
    email: attributes.email,
    avatarUrl: attributes.avatar_url,
    userId: attributes.user_id,
    isInvited: Boolean(attributes.user_id),
  }
}

export function usePersonMutations() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()
  const slug = workspace.slug

  const createPerson = useMutation({
    mutationFn: (input: CreatePersonInput) =>
      fetchWithAuth<PersonRecordResponse>(`/api/v1/workspaces/${slug}/records`, {
        method: "POST",
        body: JSON.stringify({
          object_type: "person",
          attributes: {
            name: input.name,
            ...(input.email ? { email: input.email } : {}),
          },
        }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["people", slug] })
    },
  })

  return {
    createPerson: {
      ...createPerson,
      mutateAsync: async (input: CreatePersonInput) => mapRecordToPerson(await createPerson.mutateAsync(input)),
    },
  }
}
